import React, { useState, useEffect } from 'react';
import { FiKey, FiActivity, FiFileText, FiCopy, FiEye, FiEyeOff, FiCheck, FiClock, FiAlertCircle } from 'react-icons/fi';
import { Card, LoaderComponent, EmptyState } from '../../components/ui';
import portalService from '../../services/portal.service';
import { fmtParede } from '../../utils/tempoCv';

interface ApiKey {
  id: string;
  apiKey: string;
  apiKeyMasked: string;
  nome: string;
  plano: string;
  rateLimit: number;
  dailyLimit: number;
  monthlyLimit: number;
  estado: string;
  dtCriacao: string;
  ultimoUso: string | null;
  planDetails: { name: string; price: number | null };
}

interface UsageData {
  keys: { total: number; active: number; ultimoUso: string | null };
  plano: { nome: string; codigo: string; rateLimit: number; dailyLimit: number; monthlyLimit: number };
  usage: { hoje: number; mes: number; limiteRestanteDia: number };
  dailyChart: Array<{ dia: string; requests: number; sucesso: number; falhou: number }>;
}

interface BillingData {
  faturas: Array<{
    mes: string; mesNum: string; plano: string; planoCodigo: string;
    preco: number; transacoes: number; estado: string;
  }>;
  resumo: { total: number; pago: number; pendente: number };
}

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  basic: 'bg-blue-100 text-blue-700',
  pro: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const PortalApiDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [activeTab, setActiveTab] = useState<'keys' | 'usage' | 'billing'>('keys');
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'API');
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [keysRes, usageRes, billingRes] = await Promise.all([
        portalService.getApiKeys(),
        portalService.getApiUsage(),
        portalService.getApiBilling(),
      ]);
      setKeys(keysRes);
      setUsage(usageRes);
      setBilling(billingRes);
    } catch (err) {
      console.error('Error loading API data:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (id: string, key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleVisibility = (id: string) => {
    const next = new Set(visibleKeys);
    if (next.has(id)) next.delete(id); else next.add(id);
    setVisibleKeys(next);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE' }).format(value);

  if (loading) return <LoaderComponent size="lg" text="A carregar dados da API..." />;

  const hasApiKeys = keys.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-pagali-gray-800">API Dashboard</h2>
        <p className="text-pagali-gray-500">Gerencie as suas API keys, uso e faturação</p>
      </div>

      {!hasApiKeys ? (
        <Card>
          <EmptyState
            icon={<FiKey className="text-5xl" />}
            title="Sem API Keys"
            description="Ainda não tem API keys configuradas. Contacte o administrador para activar o acesso à API."
          />
        </Card>
      ) : (
        <>
          {/* Usage Stats Cards */}
          {usage && usage.plano && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <div className="text-center">
                  <p className="text-sm text-pagali-gray-500">Plano Actual</p>
                  <p className={`text-lg font-bold mt-1 px-3 py-1 rounded-full inline-block ${PLAN_COLORS[usage.plano?.codigo] || PLAN_COLORS.free}`}>
                    {usage.plano?.nome || 'Free'}
                  </p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-pagali-gray-500">Requests Hoje</p>
                  <p className="text-2xl font-bold text-pagali-gray-800">{usage.usage?.hoje ?? 0}</p>
                  <p className="text-xs text-pagali-gray-400">de {usage.plano?.dailyLimit}/dia</p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-pagali-gray-500">Requests Este Mês</p>
                  <p className="text-2xl font-bold text-pagali-gray-800">{usage.usage?.mes ?? 0}</p>
                  <p className="text-xs text-pagali-gray-400">de {usage.plano?.monthlyLimit || '∞'}/mês</p>
                </div>
              </Card>
              <Card>
                <div className="text-center">
                  <p className="text-sm text-pagali-gray-500">Keys Activas</p>
                  <p className="text-2xl font-bold text-green-600">{usage.keys?.active ?? 0}</p>
                  <p className="text-xs text-pagali-gray-400">de {usage.keys?.total ?? 0} total</p>
                </div>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-pagali-gray-100 rounded-xl p-1">
            {[
              { id: 'keys' as const, label: 'API Keys', icon: <FiKey /> },
              { id: 'usage' as const, label: 'Uso', icon: <FiActivity /> },
              { id: 'billing' as const, label: 'Faturação', icon: <FiFileText /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-pagali-primary shadow-sm'
                    : 'text-pagali-gray-500 hover:text-pagali-gray-700'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'keys' && (
            <Card>
              <h3 className="font-semibold text-pagali-gray-800 mb-4">As suas API Keys</h3>
              <div className="space-y-3">
                {keys.map((key) => (
                  <div key={key.id} className="p-4 border border-pagali-gray-100 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pagali-primary/10 rounded-lg flex items-center justify-center">
                          <FiKey className="text-pagali-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-pagali-gray-800">{key.nome}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${PLAN_COLORS[key.plano] || PLAN_COLORS.free}`}>
                            {key.planDetails?.name || key.plano}
                          </span>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        key.estado === 'active' ? 'bg-green-100 text-green-700' :
                        key.estado === 'suspended' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {key.estado === 'active' ? 'Activa' : key.estado === 'suspended' ? 'Suspensa' : 'Expirada'}
                      </span>
                    </div>

                    {/* API Key display */}
                    <div className="flex items-center gap-2 bg-pagali-gray-50 rounded-lg p-3">
                      <code className="flex-1 text-sm text-pagali-gray-700 font-mono">
                        {visibleKeys.has(key.id) ? key.apiKey : key.apiKeyMasked}
                      </code>
                      <button onClick={() => toggleVisibility(key.id)}
                        className="p-1.5 hover:bg-pagali-gray-200 rounded-lg transition-colors">
                        {visibleKeys.has(key.id) ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                      <button onClick={() => copyKey(key.id, key.apiKey)}
                        className="p-1.5 hover:bg-pagali-gray-200 rounded-lg transition-colors">
                        {copiedId === key.id ? <FiCheck size={16} className="text-green-500" /> : <FiCopy size={16} />}
                      </button>
                    </div>

                    {/* Limits */}
                    <div className="flex gap-4 mt-3 text-xs text-pagali-gray-500">
                      <span>{key.rateLimit} req/min</span>
                      <span>{key.dailyLimit} req/dia</span>
                      <span>{key.monthlyLimit || '∞'} req/mês</span>
                      {key.ultimoUso && (
                        <span>Último uso: {new Date(key.ultimoUso).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === 'usage' && usage && (
            <Card>
              <h3 className="font-semibold text-pagali-gray-800 mb-4">Uso da API (últimos 7 dias)</h3>
              {usage.dailyChart.length === 0 ? (
                <p className="text-pagali-gray-500 text-center py-8">Sem dados de uso nos últimos 7 dias</p>
              ) : (
                <div className="space-y-2">
                  {/* Simple bar chart */}
                  {usage.dailyChart.map((day) => {
                    const maxRequests = Math.max(...usage.dailyChart.map(d => d.requests), 1);
                    const widthPercent = (day.requests / maxRequests) * 100;
                    return (
                      <div key={day.dia} className="flex items-center gap-3">
                        <span className="text-xs text-pagali-gray-500 w-20 text-right">
                          {fmtParede(day.dia, { weekday: 'short', day: 'numeric' }, 'pt-PT')}
                        </span>
                        <div className="flex-1 bg-pagali-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="h-full bg-pagali-primary/70 rounded-full flex items-center px-2"
                            style={{ width: `${Math.max(widthPercent, 5)}%` }}>
                            <span className="text-xs text-white font-medium">{day.requests}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 text-xs w-24">
                          <span className="text-green-600">{day.sucesso} ok</span>
                          {day.falhou > 0 && <span className="text-red-500">{day.falhou} fail</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rate Limit Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <FiAlertCircle /> Limites do Plano {usage.plano.nome}
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-600">Rate Limit</p>
                    <p className="font-bold text-blue-800">{usage.plano.rateLimit} req/min</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Limite Diário</p>
                    <p className="font-bold text-blue-800">{usage.plano.dailyLimit} req/dia</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Limite Mensal</p>
                    <p className="font-bold text-blue-800">{usage.plano.monthlyLimit || '∞'} req/mês</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'billing' && billing && (
            <Card>
              <h3 className="font-semibold text-pagali-gray-800 mb-4">Faturação</h3>

              {/* Billing Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-pagali-gray-50 rounded-xl text-center">
                  <p className="text-sm text-pagali-gray-500">Total</p>
                  <p className="text-xl font-bold text-pagali-gray-800">{formatCurrency(billing.resumo.total)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <p className="text-sm text-green-600">Pago</p>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(billing.resumo.pago)}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <p className="text-sm text-amber-600">Pendente</p>
                  <p className="text-xl font-bold text-amber-700">{formatCurrency(billing.resumo.pendente)}</p>
                </div>
              </div>

              {/* Invoices Table */}
              {billing.faturas.length === 0 ? (
                <p className="text-pagali-gray-500 text-center py-8">Sem faturas (plano Free não tem custos)</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-pagali-gray-100">
                        <th className="text-left py-3 px-4 font-medium text-pagali-gray-500">Mês</th>
                        <th className="text-left py-3 px-4 font-medium text-pagali-gray-500">Plano</th>
                        <th className="text-right py-3 px-4 font-medium text-pagali-gray-500">Transações</th>
                        <th className="text-right py-3 px-4 font-medium text-pagali-gray-500">Valor</th>
                        <th className="text-center py-3 px-4 font-medium text-pagali-gray-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.faturas.map((fatura) => (
                        <tr key={fatura.mesNum} className="border-b border-pagali-gray-50 hover:bg-pagali-gray-50">
                          <td className="py-3 px-4 font-medium text-pagali-gray-800 capitalize">{fatura.mes}</td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${PLAN_COLORS[fatura.planoCodigo] || PLAN_COLORS.free}`}>
                              {fatura.plano}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-pagali-gray-600">{fatura.transacoes}</td>
                          <td className="py-3 px-4 text-right font-semibold">{formatCurrency(fatura.preco)}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                              fatura.estado === 'pago' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {fatura.estado === 'pago' ? <FiCheck size={12} /> : <FiClock size={12} />}
                              {fatura.estado === 'pago' ? 'Pago' : 'Pendente'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PortalApiDashboard;
